import {Button, Search} from '@dabafinance/react-components';
import styles from './academyCodes.module.scss';
import {Dropdown, Form, Pagination, Table, Tag} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {
  DELETE_COUPON,
  GET_ALL_ACADEMY_CODE_STATS,
  GET_ALL_ACADEMY_CODES,
} from './resolvers';
import {useCallback, useState} from 'react';
import DBModal from 'components/modal/modal';
import {CreateAcademyCode} from './createAcademyCode';
import dotsIcon from 'images/dots.svg';
import moment from 'moment';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import PercentageCard from 'pages/adminUser/card/PercentageCard';
import {debounce, sanitizeInput} from 'utils/helper';
import {ExportAcademyCodes} from './ExportAcademyCodes';
import {AcademyCodeUsers} from './AcademyCodeUsers';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

export const AcademyCodes = () => {
  const [exportModal, setExportModal] = useState({open: false, id: null});
  const [openUsersModal, setOpenUsersModal] = useState({open: false, id: null});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchValue] = useState('');

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.AcademyCodes),
  );

  const [deleteModal, setDeleteModal] = useState({deleteId: null, open: false});
  const [openCreateModal, setOpenCreateModal] = useState({
    open: false,
    id: null,
  });
  const [form] = Form.useForm();

  const {data: stats, loading: loadingStats} = useQuery(
    GET_ALL_ACADEMY_CODE_STATS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  const {data, refetch, loading} = useQuery(GET_ALL_ACADEMY_CODES, {
    variables: {
      filter: {
        page: 1,
        limit: 10,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const [deleteCoupon, {loading: loadingDelete}] = useMutation(DELETE_COUPON, {
    onError: error => toast.error(error.message),
    errorPolicy: 'all',
  });

  const handleSearch = value => {
    const sanitizedValue = sanitizeInput(value);
    setCurrentPage(1);
    setLimit(10);
    setSearchValue(sanitizedValue);
    if (!sanitizedValue) {
      refetch({
        filter: {
          page: 1,
          limit: 10,
        },
      });
      return;
    } else
      refetch({
        filter: {
          page: 1,
          limit: 10,
          coupon: sanitizedValue,
        },
      });
  };

  const optimizedSearchCall = useCallback(debounce(handleSearch), []);

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      filter: {
        page,
        limit: pageSize,
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'coupon',
      key: 'coupon',
    },
    {
      title: 'Type',
      dataIndex: 'promoCodeType',
      key: 'promoCodeType',
      render: (text, record) => (
        <Tag color={record?.promoCodeType === 'LIMITED' ? 'default' : 'black'}>
          {record?.promoCodeType}
        </Tag>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'ownerType',
      key: 'ownerType',
      render: (text, record) => {
        const ownerType = record?.ownerType || 'PLATFORM';
        if (ownerType === 'PLATFORM') {
          return <Tag color="default">Platform</Tag>;
        }
        const name =
          `${record?.owner?.firstName || ''} ${
            record?.owner?.lastName || ''
          }`.trim() ||
          record?.owner?.email ||
          '-';
        return (
          <div>
            <div>{name}</div>
            <Tag color={ownerType === 'AGENT' ? 'blue' : 'purple'}>
              {ownerType === 'AGENT' ? 'Sales Agent' : 'Referral Partner'}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Total Redemption',
      dataIndex: 'totalRedemption',
      key: 'totalRedemption',
    },
    {
      title: 'Promo Code Status',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      render: (text, record) => (
        <Tag color={record?.isEnabled ? 'green' : 'red'}>
          {record?.isEnabled ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (text, record) =>
        record?.startDate
          ? moment(record?.startDate).format('DD MMM, YYYY. hh:mm A')
          : '-',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text, record) =>
        record?.endDate
          ? moment(record?.endDate).format('DD MMM, YYYY. hh:mm A')
          : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Dropdown
          trigger="click"
          menu={{
            items: [
              {
                key: 1,
                label: 'Edit Promo Code',
                onClick: () => setOpenCreateModal({open: true, id: record?.id}),
              },
              {
                key: 3,
                label: 'View Users',
                onClick: () => {
                  setOpenUsersModal({
                    open: true,
                    id: record?.id,
                    name: record?.coupon,
                  });
                },
              },
              {
                key: 2,
                label: 'Delete Promo Code',
                disabled: !pageOperations?.delete,
                onClick: () =>
                  setDeleteModal({open: true, deleteId: record?.id}),
              },
            ],
          }}>
          <img src={dotsIcon} alt="dots" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        <h1>Daba Academy Promo Codes</h1>
        <div className={styles.navButtons}>
          <Button
            onClick={() => {
              form.resetFields();
              setOpenCreateModal({open: true});
            }}
            disabled={!pageOperations?.create}
            type="secondary"
            className={styles.button}
            label="Create New Promo Code"
          />
        </div>
      </div>
      <div className={styles.cards}>
        <PercentageCard
          amount={stats?.getCouponStats?.data?.totalUsedPromoCodes}
          label="Total used Promo Codes"
          loading={loadingStats}
        />
        <PercentageCard
          amount={stats?.getCouponStats?.data?.activePromoCodes}
          label="Active Promo Codes"
          loading={loadingStats}
        />{' '}
        <PercentageCard
          amount={stats?.getCouponStats?.data?.expiredPromoCodes}
          label="Expired Promo Codes"
          loading={loadingStats}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.filterHeader}>
          <h2>Promo Codes</h2>
          <div className={styles.filterSection}>
            <Search
              value={searchValue}
              placeholder="Search by Names"
              onChange={e => {
                setSearchValue(e);
                optimizedSearchCall(e);
              }}
              id={'user-search'}
            />
            <Button
              onClick={() => setExportModal({open: true})}
              type="secondary"
              disabled={!pageOperations?.export}
              label="Export"
            />
          </div>
        </div>
        <Table
          columns={columns}
          loading={loading}
          className={styles.table}
          pagination={false}
          dataSource={data?.getAllCoupons?.data}
        />
        <Pagination
          className="ant-table-pagination"
          current={currentPage}
          onChange={handlePageChange}
          total={data?.getAllCoupons?.pagination?.total}
          pageSize={limit}
          showSizeChanger
        />
      </div>
      <DBModal
        isOpen={openCreateModal.open}
        handleClose={() => {
          setOpenCreateModal({open: false});
        }}
        content={
          <CreateAcademyCode
            close={() => {
              setOpenCreateModal({open: false});
              form.resetFields();
            }}
            id={openCreateModal.id}
            form={form}
            refetch={refetch}
          />
        }
      />
      <ConfirmModal
        openModal={deleteModal.open}
        setOpenModal={() => setDeleteModal({open: false, deleteId: null})}
        loading={loadingDelete}
        title="Delete Promo Code"
        confirmText="Are you sure you want to delete this promo code? This action cannot be undone!"
        handleOperation={() =>
          deleteCoupon({
            variables: {
              deleteCouponId: deleteModal.deleteId,
            },
          })
            .then(({data: {deleteCoupon}}) => {
              if (
                deleteCoupon.__typename === 'Error' ||
                deleteCoupon.statusCode === 400
              ) {
                toast.error(deleteCoupon.message);
              } else {
                toast.success('Promo Code Deleted Successfully.');
                refetch();
                setDeleteModal({open: false, deleteId: null});
              }
            })
            .catch(error => {
              toast.error(error);
            })
        }
      />
      <DBModal
        isOpen={exportModal.open}
        handleClose={() => {
          setExportModal({open: false});
        }}
        content={
          <ExportAcademyCodes close={() => setExportModal({open: false})} />
        }
      />
      <DBModal
        isOpen={openUsersModal.open}
        handleClose={() => {
          setOpenUsersModal({open: false});
        }}
        width={900}
        content={
          <AcademyCodeUsers
            id={openUsersModal.id}
            name={openUsersModal?.name}
            close={() => setOpenUsersModal({open: false})}
          />
        }
      />
    </div>
  );
};
